import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import {
  createQueryRetryPredicate,
  getHttpStatus,
  isForbiddenError,
  UserFacingError,
} from '@/lib/api/errors';

export const ReactQueryClientProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [queryClient] = useState(() => {
    const showErrorSnackbar = (error: unknown, errorMessage?: string) => {
      if (typeof errorMessage === 'string' && errorMessage) {
        return enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      }

      if (error instanceof UserFacingError) {
        return enqueueSnackbar(error.message, {
          variant: 'error',
          autoHideDuration: 6000,
        });
      }

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          return enqueueSnackbar(
            "There's something wrong with your request. Double check the data you're submitting and try again.",
            {
              variant: 'error',
              autoHideDuration: 4500,
            }
          );
        }

        if (error.response?.status === 403) {
          return enqueueSnackbar(
            "You don't have permission to perform this action.",
            {
              variant: 'error',
              autoHideDuration: 4000,
            }
          );
        }

        return enqueueSnackbar(
          `Error communicating with the server: ${error.message}`,
          {
            variant: 'error',
          }
        );
      }

      console.error(error);
      enqueueSnackbar('Something went wrong', {
        variant: 'error',
      });
    };

    return new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: createQueryRetryPredicate({ maxFailures: 2 }),
          // A forbidden read is page state, not an event: it propagates to
          // the route-level ForbiddenBoundary, which renders NoAccessState
          // in place of the page content. This must not consult query.meta:
          // meta lives on the shared query, so observers of the same key
          // would overwrite each other's choice. Auxiliary observers opt out
          // per observer with `throwOnError: false`.
          throwOnError: (error) => isForbiddenError(error),
        },
        mutations: {
          // Default feedback for failed writes; a hook-level onError replaces it.
          onError: (error) => showErrorSnackbar(error),
        },
      },
      queryCache: new QueryCache({
        onError: (error, query) => {
          if (query.meta?.ignoreError && axios.isAxiosError(error)) {
            return;
          }
          const silencedStatuses = query.meta?.ignoreErrorStatuses;
          if (silencedStatuses) {
            const status = getHttpStatus(error);
            if (status !== undefined && silencedStatuses.includes(status)) {
              return;
            }
          }
          // Forbidden reads render as an in-place restricted state, never a toast.
          if (isForbiddenError(error)) {
            return;
          }
          showErrorSnackbar(error, query.meta?.errorMessage);
        },
      }),
    });
  });

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
